import type { IPopup } from "@mm-frontend/mm-web-ui-kit";
import { EAppSearchResultsMode } from "../models/EAppSearchResultsMode";
import { getSuggestions } from "@/shared/apollo-api/api/suggestions";
import type { TSearchSuggestions } from "../models/TSearchSuggestions";
import { getSuggestionsMapper } from "../mappers/getSuggestionsMapper";
import { ref } from 'vue';
import type { ISearchHistory } from "../interface/searchHistory";

export class AppSearchController {
  constructor(private searchHistoryService: ISearchHistory) {
    this.searchHistoryService = searchHistoryService;
  }

  public popup: IPopup | null = null;

  public setPopup(popup: IPopup) {
    this.popup = popup;
  }

  public mode = ref<EAppSearchResultsMode>(EAppSearchResultsMode.MENU);
  public isOpened = ref(false);

  public setMode(mode: EAppSearchResultsMode) {
    this.mode.value = mode;

    if (mode === EAppSearchResultsMode.POPUP && this.isOpened.value) {
      this.popup?.show();
    } else {
      this.popup?.close();
    }

    if (!this.isOpened.value) {
      this.close();
    }
  }

  public close() {
    if (this.mode.value === EAppSearchResultsMode.POPUP) {
      this.popup?.close();
    }

    this.isOpened.value = false;
  }

  public async open() {
    if (this.mode.value === EAppSearchResultsMode.POPUP) {
      this.popup?.show();
    }
    
    this.isOpened.value = true;
  }

  public toggle() {
    if (this.isOpened.value) {
      this.close();
    } else {
      this.open();
    }
  }

  
  public query = ref('');
  
  public clearQuery() {
    this.query.value = '';
  }

  public isSuggestionsLoaded = ref(false);
  public isSuggestionsEmpty = ref(false);
  public lastFetchedQuery = ref('');
  public suggestions = ref<TSearchSuggestions>({ blocks: [] });
  public isSearchInputFocused = ref(false);
  public previousTextItems = ref<string[]>([]);
  
  private async getSuggestions() {
    if (this.isSuggestionsLoaded.value && this.lastFetchedQuery.value === this.query.value) return;
    
    this.previousTextItems.value = this.suggestions.value.blocks.find(block => block.blockType === 'text')?.items.map(item => item.title) ?? [];
    
    this.isSuggestionsLoaded.value = false;
    this.lastFetchedQuery.value = this.query.value;
    
    const suggestions = await getSuggestions({
      text: this.query.value ?? '',
      catalogCardSuggestionsLimit: 0,
      categorySuggestionsLimit: 4,
      popularSuggestionsLimit: 4,
      shopSuggestionsLimit: 4,
      textSuggestionsLimit: 4,
    });
    
    this.suggestions.value = getSuggestionsMapper(suggestions, this.previousTextItems.value);
    this.isSuggestionsLoaded.value = true;
    this.isSuggestionsEmpty.value = this.suggestions.value.blocks.every(block => block.items.length === 0);
  }

  public async getSuggestionsAndShowResults() {
    await this.getSuggestions();

    // Если за время загрузки юзер убрал фокус с поля
    // не показывать результаты
    if (!this.isSearchInputFocused.value) return;

    // Открываем результаты поиска, если они не открыты и есть предложения
    if (!this.isOpened.value && !this.isSuggestionsEmpty.value) {
      this.open();
      this.searchHistoryService.addToSearchHistory(this.query.value);
    }
    // Закрываем результаты поиска, если они открыты и предложения пустые
    else if (this.isOpened.value && this.isSuggestionsEmpty.value) {
      this.close();
    }
  }
}